import {
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
	HttpErrorResponse,
} from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { JwtInterceptor } from "@auth0/angular-jwt";
import { Store } from "@ngxs/store";
import { Observable, throwError, BehaviorSubject } from "rxjs";
import {
	catchError,
	tap,
	switchMap,
	finalize,
	shareReplay,
	filter,
	take,
	timeout,
} from "rxjs/operators";
import { StoredAuthUserRefreshTokenAction } from "../state/stored-auth.actions";
import { StoredAuthState } from "../state/stored-auth.state";
import { SessionWarningService } from "./session-warning.service";

const REFRESH_TOKEN_ENDPOINTS = ["refresh-token", "refresh_token"];
const REFRESH_TIMEOUT = 10000; // 10 seconds
const TOKEN_REFRESH_BUFFER = 300; // 5 minutes before expiry (increased from 1 minute)

interface QueuedRequest {
	request: HttpRequest<unknown>;
	next: HttpHandler;
	subject: BehaviorSubject<HttpEvent<unknown>>;
}

function isRefreshTokenRequest(url: string): boolean {
	return REFRESH_TOKEN_ENDPOINTS.some((endpoint) =>
		url.toLowerCase().includes(endpoint.toLowerCase()),
	);
}

function shouldInterceptRequest(
	jwtInterceptor: JwtInterceptor,
	request: HttpRequest<unknown>,
): boolean {
	return (
		jwtInterceptor.isAllowedDomain(request) &&
		!jwtInterceptor.isDisallowedRoute(request)
	);
}

function isTokenExpiredSafely(
	jwtInterceptor: JwtInterceptor,
	token: string | undefined,
): boolean {
	if (!token || token === "undefined" || token.trim() === "") {
		console.debug("RefreshTokenInterceptor: No valid token found");
		return true;
	}

	try {
		const isExpired = jwtInterceptor.jwtHelper.isTokenExpired(token);
		console.debug("RefreshTokenInterceptor: Token expired check:", isExpired);
		return isExpired;
	} catch (error) {
		console.warn("RefreshTokenInterceptor: Error checking token expiration:", error);
		return true;
	}
}

function isTokenExpiringSoon(
	jwtInterceptor: JwtInterceptor,
	token: string | undefined,
): boolean {
	if (!token || token === "undefined" || token.trim() === "") {
		console.debug(
			"RefreshTokenInterceptor: No valid token found for expiring soon check",
		);
		return true;
	}

	try {
		const expirationDate = jwtInterceptor.jwtHelper.getTokenExpirationDate(token);
		if (!expirationDate) {
			console.debug("RefreshTokenInterceptor: No expiration date found in token");
			return true;
		}

		const now = new Date();
		const timeUntilExpiry = expirationDate.getTime() - now.getTime();
		const isExpiringSoon = timeUntilExpiry <= TOKEN_REFRESH_BUFFER * 1000;

		console.debug(
			"RefreshTokenInterceptor: Time until expiry (ms):",
			timeUntilExpiry,
		);
		console.debug(
			"RefreshTokenInterceptor: Buffer threshold (ms):",
			TOKEN_REFRESH_BUFFER * 1000,
		);
		console.debug("RefreshTokenInterceptor: Token expiring soon:", isExpiringSoon);

		return isExpiringSoon;
	} catch (error) {
		console.warn(
			"RefreshTokenInterceptor: Error checking token expiration buffer:",
			error,
		);
		return true;
	}
}

@Injectable({
	providedIn: "root",
})
export class RefreshTokenInterceptor implements HttpInterceptor {
	private readonly store = inject(Store);
	private readonly jwtInterceptor = inject(JwtInterceptor);
	private readonly sessionWarningService = inject(SessionWarningService);

	private refreshTokenObservable: Observable<void> | null = null;
	private isRefreshing = false;
	private requestQueue: QueuedRequest[] = [];
	private readonly refreshSubject = new BehaviorSubject<boolean>(false);

	intercept(
		request: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown>> {
		// Skip refresh token requests
		if (isRefreshTokenRequest(request.url)) {
			return next.handle(request);
		}

		// Skip requests that shouldn't be intercepted
		if (!shouldInterceptRequest(this.jwtInterceptor, request)) {
			return next.handle(request);
		}

		const token = this.store.selectSnapshot(StoredAuthState.authIdToken);
		const isAuthenticated = this.store.selectSnapshot(
			StoredAuthState.isUserAuthenticated,
		);

		// If user is not authenticated, don't process
		if (!isAuthenticated) {
			return next.handle(request);
		}

		// If already refreshing, queue the request
		if (this.isRefreshing) {
			console.debug(
				"RefreshTokenInterceptor: Already refreshing, queueing request for",
				request.url,
			);
			return this.handleTokenRefresh(request, next);
		}

		// Check if token is expired or expiring soon
		const isExpired = isTokenExpiredSafely(this.jwtInterceptor, token);
		const isExpiringSoon = isTokenExpiringSoon(this.jwtInterceptor, token);

		// Debug logging to identify the issue
		if (isExpired || isExpiringSoon) {
			console.debug(
				"RefreshTokenInterceptor: Token refresh triggered for",
				request.url,
			);
			console.debug(
				"RefreshTokenInterceptor: Token expired:",
				isExpired,
				"Token expiring soon:",
				isExpiringSoon,
			);
			console.debug(
				"RefreshTokenInterceptor: Currently refreshing:",
				this.isRefreshing,
			);
		}

		if (!isExpired && !isExpiringSoon) {
			return this.processRequest(request, next);
		}

		// Token needs refresh
		return this.handleTokenRefresh(request, next);
	}

	private processRequest(
		request: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown>> {
		return this.jwtInterceptor.intercept(request, next).pipe(
			catchError((error: HttpErrorResponse) => {
				// If we get 401, try to refresh token
				if (error.status === 401) {
					console.debug(
						"RefreshTokenInterceptor: 401 error, attempting token refresh",
					);
					return this.handleTokenRefresh(request, next);
				}
				return throwError(() => error);
			}),
		);
	}

	private handleTokenRefresh(
		request: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown>> {
		const subject = new BehaviorSubject<HttpEvent<unknown>>(null as any);

		// Add request to queue
		this.requestQueue.push({
			request,
			next,
			subject,
		});

		// Start refresh if not already in progress
		if (!this.isRefreshing) {
			this.startTokenRefresh();
		}

		// Return observable that completes when refresh is done
		return this.refreshSubject.pipe(
			filter((refreshCompleted) => refreshCompleted),
			take(1),
			switchMap(() => this.processQueuedRequest(request, next)),
			catchError((error: HttpErrorResponse) => {
				console.error(
					"RefreshTokenInterceptor: Error processing request after refresh:",
					error,
				);
				return throwError(() => error);
			}),
		);
	}

	private startTokenRefresh(): void {
		if (this.isRefreshing) {
			return;
		}

		this.isRefreshing = true;
		this.refreshSubject.next(false);

		console.debug("RefreshTokenInterceptor: Starting token refresh");

		const refreshTokenAction = new StoredAuthUserRefreshTokenAction();

		this.refreshTokenObservable = this.store.dispatch(refreshTokenAction).pipe(
			timeout(REFRESH_TIMEOUT),
			tap(() => {
				console.debug(
					"RefreshTokenInterceptor: Token refresh completed successfully",
				);

				// Start session warnings for the new token
				const newToken = this.store.selectSnapshot(StoredAuthState.authIdToken);
				if (newToken) {
					this.sessionWarningService.startSessionWarnings(newToken);
				}
			}),
			catchError((error: HttpErrorResponse) => {
				console.error("RefreshTokenInterceptor: Token refresh failed:", error);
				this.handleRefreshFailure(error);
				return throwError(() => error);
			}),
			finalize(() => {
				this.cleanupRefresh();
			}),
			shareReplay({ bufferSize: 1, refCount: true }),
		);

		this.refreshTokenObservable.subscribe({
			next: () => {
				// Wait for state to be updated
				setTimeout(() => {
					this.processQueuedRequests();
				}, 100);
			},
			error: (error) => {
				this.rejectQueuedRequests(error);
			},
		});
	}

	private processQueuedRequests(): void {
		console.debug(
			`RefreshTokenInterceptor: Processing ${this.requestQueue.length} queued requests`,
		);

		const queue = [...this.requestQueue];
		this.requestQueue = [];

		queue.forEach((queuedRequest) => {
			this.processQueuedRequest(
				queuedRequest.request,
				queuedRequest.next,
			).subscribe({
				next: (event) => queuedRequest.subject.next(event),
				error: (error) => queuedRequest.subject.error(error),
				complete: () => queuedRequest.subject.complete(),
			});
		});

		this.refreshSubject.next(true);
	}

	private processQueuedRequest(
		request: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown>> {
		// Verify token was refreshed
		const token = this.store.selectSnapshot(StoredAuthState.authIdToken);
		const isAuthenticated = this.store.selectSnapshot(
			StoredAuthState.isUserAuthenticated,
		);

		if (!isAuthenticated || isTokenExpiredSafely(this.jwtInterceptor, token)) {
			console.error("RefreshTokenInterceptor: Token still invalid after refresh");
			return throwError(
				() =>
					new HttpErrorResponse({
						status: 401,
						statusText: "Unauthorized",
						error: "Token refresh failed - user not authenticated",
					}),
			);
		}

		// Process request with new token
		return this.jwtInterceptor.intercept(request, next).pipe(
			catchError((error: HttpErrorResponse) => {
				console.error(
					"RefreshTokenInterceptor: Request failed after token refresh:",
					error,
				);
				return throwError(() => error);
			}),
		);
	}

	private rejectQueuedRequests(error: HttpErrorResponse): void {
		console.debug(
			`RefreshTokenInterceptor: Rejecting ${this.requestQueue.length} queued requests`,
		);

		const queue = [...this.requestQueue];
		this.requestQueue = [];

		queue.forEach((queuedRequest) => {
			queuedRequest.subject.error(error);
		});
	}

	private handleRefreshFailure(_error: HttpErrorResponse): void {
		console.error("RefreshTokenInterceptor: Refresh failed, clearing auth state");

		// Clear any stale auth state
		// Note: This might trigger logout actions depending on your auth flow

		// Dispatch logout action if needed
		// this.store.dispatch(new LogoutAction());
	}

	private cleanupRefresh(): void {
		console.debug("RefreshTokenInterceptor: Cleaning up refresh state");

		this.isRefreshing = false;
		this.refreshTokenObservable = null;

		// Reset refresh subject for next refresh cycle
		setTimeout(() => {
			this.refreshSubject.next(false);
		}, 100);
	}
}
